import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowLeft, Send, Lock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/Skeleton";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";
import {
    STATUS_LABELS, STATUS_TONES, CATEGORY_LABELS, formatTicketNumber,
} from "@/lib/supportHelpers";

const BODY_MAX = 4000;

const fmtStamp = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        if (diff < 1000 * 60 * 60 * 24)
            return formatDistanceToNow(d, { addSuffix: true });
        return format(d, "d MMM yyyy, HH:mm");
    } catch { return iso; }
};

export const MessageBubble = ({ message, viewerRole }) => {
    const mine = (viewerRole === "user" && message.author_role === "user")
        || (viewerRole === "admin" && message.author_role === "admin");
    const align = mine ? "items-end" : "items-start";
    const bubbleTone = mine
        ? "bg-primary text-primary-foreground"
        : "bg-secondary text-foreground";
    return (
        <li className={cn("flex flex-col gap-1.5", align)} data-testid={`support-msg-${message.id}`}>
            <div className={cn("flex items-center gap-2 text-xs text-muted-foreground", mine && "flex-row-reverse")}>
                {message.author_role === "admin" ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        <Sparkles className="h-3 w-3" />
                        Happy Lab
                    </span>
                ) : (
                    <InitialsAvatar name={message.author_name} className="h-6 w-6 text-[10px]" dataTestId={`support-msg-avatar-${message.id}`} />
                )}
                <span className="font-medium text-foreground">{message.author_name}</span>
                <span>· {fmtStamp(message.created_at)}</span>
            </div>
            <div className={cn(
                "max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm",
                bubbleTone,
                mine ? "rounded-tr-md" : "rounded-tl-md"
            )}>
                {message.content}
            </div>
        </li>
    );
};

/**
 * Shared thread view used by both /support/:id and /admin/support/:id.
 * `mode` is "user" or "admin" — controls API path + composer role + side rail.
 */
export const TicketThreadView = ({ mode = "user", rightRail }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const [data, setData] = useState(null);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    useDocumentTitle("Support");

    const basePath = mode === "admin" ? "/admin/support/tickets" : "/support/tickets";

    useEffect(() => {
        let cancelled = false;
        api.get(`${basePath}/${id}`)
            .then(async (r) => {
                if (cancelled) return;
                setData(r.data);
                // Stamp last-viewed → backend reset; refresh user so the nav dot clears.
                if (mode === "user") {
                    try { await refreshUser(); }
                    catch (refreshErr) {
                        // Non-fatal — the ticket loaded; just log so it's visible.
                        console.error("[SupportThread] refreshUser after ticket view failed", refreshErr);
                    }
                }
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    toast.error("Ticket not found");
                    navigate(mode === "admin" ? "/admin/support" : "/support", { replace: true });
                }
            });
        return () => { cancelled = true; };
    }, [id, basePath, mode, navigate, refreshUser]);

    useEffect(() => {
        if (data && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [data]);

    const sendReply = async () => {
        const content = reply.trim();
        if (!content || sending) return;
        setSending(true);
        try {
            const { data: msg } = await api.post(`${basePath}/${id}/messages`, { content });
            // Refetch full thread to capture status flips + last_message_by.
            const { data: fresh } = await api.get(`${basePath}/${id}`);
            setData(fresh);
            setReply("");
            toast.success("Reply sent");
            if (mode === "user") {
                try { await refreshUser(); }
                catch (refreshErr) {
                    // Non-fatal — the reply went through; just log so it's visible.
                    console.error("[SupportThread] refreshUser after reply failed", refreshErr);
                }
            }
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                toast.error(typeof detail === "string" ? detail : "This ticket is closed.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Could not send reply");
            }
        } finally {
            setSending(false);
        }
    };

    const ticket = data?.ticket;
    const isClosed = ticket?.status === "closed";

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="support-thread-page">
                <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="support-thread-back">
                    <Link to={mode === "admin" ? "/admin/support" : "/support"}>
                        <ArrowLeft className="h-4 w-4" />
                        {mode === "admin" ? "Support queue" : "Your tickets"}
                    </Link>
                </Button>

                <div className={cn("grid gap-6", rightRail ? "lg:grid-cols-[1fr_18rem]" : "")}>
                    <div className="space-y-6 min-w-0">
                        {!ticket ? (
                            <Skeleton className="h-20 w-full rounded-2xl" />
                        ) : (
                            <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-sm text-muted-foreground tabular-nums" data-testid="support-thread-number">
                                        {formatTicketNumber(ticket.ticket_number)}
                                    </span>
                                    <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", STATUS_TONES[ticket.status])} data-testid="support-thread-status">
                                        {STATUS_LABELS[ticket.status]}
                                    </span>
                                    <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs text-foreground" data-testid="support-thread-category">
                                        {CATEGORY_LABELS[ticket.category] || ticket.category}
                                    </span>
                                </div>
                                <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="support-thread-subject">
                                    {ticket.subject}
                                </h1>
                                <p className="text-xs text-muted-foreground">
                                    Created {fmtStamp(ticket.created_at)}
                                    {mode === "admin" && <> · by <span className="font-medium text-foreground">{ticket.user_email}</span></>}
                                </p>
                            </div>
                        )}

                        <Card className="border-border/80 shadow-sm" data-testid="support-thread-messages-card">
                            <CardContent className="p-5">
                                {!data ? (
                                    <div className="space-y-3">
                                        {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
                                    </div>
                                ) : (
                                    <ul className="space-y-5">
                                        {data.messages.map((m) => (
                                            <MessageBubble key={m.id} message={m} viewerRole={mode} />
                                        ))}
                                        <div ref={bottomRef} />
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        {/* Composer or closed banner */}
                        {isClosed ? (
                            <Card className="border-border bg-secondary" data-testid="support-thread-closed-banner">
                                <CardContent className="p-4 text-sm text-muted-foreground inline-flex items-center gap-2">
                                    <Lock className="h-4 w-4" />
                                    This ticket is closed. Raise a new one if you need further help.
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border-border/80 shadow-sm" data-testid="support-thread-composer">
                                <CardContent className="p-4 space-y-2">
                                    <Textarea
                                        value={reply}
                                        onChange={(e) => setReply(e.target.value.slice(0, BODY_MAX))}
                                        placeholder={mode === "admin" ? "Reply to the user…" : "Reply…"}
                                        rows={4}
                                        className="min-h-24 resize-y"
                                        data-testid="support-thread-textarea"
                                    />
                                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                                        <span className="tabular-nums">{reply.length}/{BODY_MAX}</span>
                                        <Button
                                            onClick={sendReply}
                                            disabled={!reply.trim() || sending}
                                            className="bg-primary hover:bg-primary-hover font-semibold"
                                            data-testid="support-thread-send"
                                        >
                                            <Send className="h-4 w-4" />
                                            {sending ? "Sending…" : "Send reply"}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right rail (admin only) */}
                    {rightRail && (
                        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start" data-testid="support-thread-rail">
                            {rightRail({ ticket, data, refresh: async () => {
                                const { data: fresh } = await api.get(`${basePath}/${id}`);
                                setData(fresh);
                            } })}
                        </aside>
                    )}
                </div>
            </div>
        </Layout>
    );
};

// Default export: user-facing thread view (no right rail).
export default function SupportThreadPage() {
    return <TicketThreadView mode="user" />;
}
